/**
 * PositionedMetricSelectorContainer - Container for positioned metric selector
 *
 * Manages position calculations and event handlers for the metric selector.
 * Uses numeric positioning for cross-platform compatibility (CSS calc/translate
 * strings don't work on React Native).
 */

import { useCallback, useMemo, useState } from "react";
import { type LayoutChangeEvent, Platform } from "react-native";

import PositionedMetricSelectorView from "./PositionedMetricSelectorView";

import { SELECTOR_PADDING } from "@/features/player-detail/constants";

/**
 * Metric option for the selector
 */
interface MetricOption {
  readonly label: string;
  readonly value: number | null;
}

/** Estimated initial selector dimensions before native onLayout measurement */
const INITIAL_SELECTOR_SIZE = { width: 80, height: 28 };

/** Minimum margin from the outer container edge for native selectors */
const MIN_EDGE_MARGIN = 4;

/**
 * Props for PositionedMetricSelector component
 */
interface PositionedMetricSelectorProps {
  readonly position: number;
  readonly chartSize: number;
  /** Padding around chart for selector labels (default 80, smaller on mobile) */
  readonly selectorPadding?: number;
  readonly metric: MetricOption | undefined;
  readonly availableMetrics: readonly MetricOption[];
  readonly isDark: boolean;
  readonly onMetricChange: (position: number, metric: MetricOption) => void;
}

/** Whether running on web platform */
const IS_WEB = Platform.OS === "web";

/**
 * Calculate selector center point around the chart
 * @param index - Axis index (0-5)
 * @param chartSize - Chart size in pixels
 * @returns Center coordinates relative to chart origin
 */
const getSelectorCenter = (
  index: number,
  chartSize: number
): { x: number; y: number } => {
  const totalAxes = 6;
  const angleOffset = -90;
  const angle = (index * (360 / totalAxes) + angleOffset) * (Math.PI / 180);
  const radius = chartSize / 2 + 20;
  const centerX = chartSize / 2;
  const centerY = chartSize / 2;

  return {
    x: centerX + radius * Math.cos(angle),
    y: centerY + radius * Math.sin(angle),
  };
};

/**
 * Container component that manages positioning and event handlers for PositionedMetricSelector.
 * @param props - Component properties
 * @param props.position - Axis position (0-5)
 * @param props.chartSize - Chart size in pixels
 * @param props.selectorPadding - Padding around chart for selector labels (default 80)
 * @param props.metric - Selected metric for this position
 * @param props.availableMetrics - All available metric options
 * @param props.isDark - Whether dark mode is active
 * @param props.onMetricChange - Handler for metric selection change
 */
const PositionedMetricSelectorContainer = ({
  position,
  chartSize,
  selectorPadding = SELECTOR_PADDING,
  metric,
  availableMetrics,
  isDark,
  onMetricChange,
}: PositionedMetricSelectorProps) => {
  const [selectorSize, setSelectorSize] = useState(INITIAL_SELECTOR_SIZE);

  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    const { width, height } = event.nativeEvent.layout;
    if (width > 0 && height > 0) {
      setSelectorSize({ width, height });
    }
  }, []);

  const containerStyle = useMemo(() => {
    const pos = getSelectorCenter(position, chartSize);
    const centerTop = selectorPadding + pos.y;
    const centerLeft = selectorPadding + pos.x;

    if (IS_WEB) {
      return {
        top: centerTop,
        left: centerLeft,
        transform: "translate(-50%, -50%)",
      };
    }

    // Native: center by offsetting half the measured selector dimensions,
    // then clamp horizontally to keep within the outer container bounds
    const outerWidth = chartSize + selectorPadding * 2;
    const rawLeft = centerLeft - selectorSize.width / 2;
    const clampedLeft = Math.max(
      MIN_EDGE_MARGIN,
      Math.min(rawLeft, outerWidth - selectorSize.width - MIN_EDGE_MARGIN)
    );

    return {
      top: centerTop - selectorSize.height / 2,
      left: clampedLeft,
    };
  }, [position, chartSize, selectorPadding, selectorSize]);

  const handleSelect = useCallback(
    (m: MetricOption) => {
      onMetricChange(position, m);
    },
    [position, onMetricChange]
  );

  // Guard against undefined metric (after hooks to follow rules of hooks)
  if (!metric) {
    return null;
  }

  return (
    <PositionedMetricSelectorView
      position={position}
      metric={metric}
      availableMetrics={availableMetrics}
      isDark={isDark}
      containerStyle={containerStyle}
      onMetricChange={handleSelect}
      onLayout={IS_WEB ? undefined : handleLayout}
    />
  );
};

export default PositionedMetricSelectorContainer;
